/**
 * Phase 18 — Invoice State Machine & Lifecycle Transitions
 * Defines legal state transitions, terminal states, and role-based action gating.
 */

import { InvoiceStatus, InvoiceRecord } from '@/types/domain/invoice-management';
import { Permission } from '@/types/domain';

export const VALID_INVOICE_TRANSITIONS: Record<InvoiceStatus, InvoiceStatus[]> = {
  RECEIVED: ['UNDER_VALIDATION', 'PENDING_MATCH', 'CANCELLED'],
  DRAFT: ['UNDER_VALIDATION', 'PENDING_MATCH', 'CANCELLED'],
  UNDER_VALIDATION: ['VALIDATION_FAILED', 'PENDING_MATCH', 'CANCELLED'],
  VALIDATION_FAILED: ['UNDER_VALIDATION', 'REJECTED', 'CANCELLED'],
  PENDING_MATCH: ['MATCHED', 'PARTIALLY_MATCHED', 'EXCEPTION', 'ON_HOLD', 'CANCELLED'],
  MATCHED: ['PENDING_APPROVAL', 'APPROVED', 'ON_HOLD', 'CANCELLED'],
  PARTIALLY_MATCHED: ['EXCEPTION', 'PENDING_APPROVAL', 'ON_HOLD', 'CANCELLED'],
  EXCEPTION: ['UNDER_VALIDATION', 'PENDING_MATCH', 'MATCHED', 'REJECTED', 'ON_HOLD', 'CANCELLED'],
  PENDING_APPROVAL: ['APPROVED', 'REJECTED', 'ON_HOLD', 'CANCELLED'],
  APPROVED: ['READY_FOR_PAYMENT', 'ON_HOLD', 'CANCELLED'],
  ON_HOLD: ['PENDING_MATCH', 'EXCEPTION', 'PENDING_APPROVAL', 'APPROVED', 'CANCELLED'],
  READY_FOR_PAYMENT: ['SCHEDULED_FOR_PAYMENT', 'ON_HOLD', 'CANCELLED'],
  SCHEDULED_FOR_PAYMENT: ['PAID', 'PARTIALLY_PAID', 'CANCELLED'],
  PAID: [],
  PARTIALLY_PAID: ['PAID'],
  REJECTED: [],
  CANCELLED: [],
};

export const TERMINAL_INVOICE_STATUSES: InvoiceStatus[] = ['PAID', 'REJECTED', 'CANCELLED'];

export function canTransitionInvoiceStatus(from: InvoiceStatus, to: InvoiceStatus): boolean {
  if (from === to) return true;
  const allowed = VALID_INVOICE_TRANSITIONS[from] || [];
  return allowed.includes(to);
}

export function isTerminalInvoiceStatus(status: InvoiceStatus): boolean {
  return TERMINAL_INVOICE_STATUSES.includes(status);
}

export type InvoiceActionType =
  | 'VALIDATE'
  | 'EXECUTE_MATCH'
  | 'APPROVE'
  | 'REJECT'
  | 'PLACE_ON_HOLD'
  | 'RELEASE_FROM_HOLD'
  | 'RESOLVE_EXCEPTION'
  | 'MARK_READY_FOR_PAYMENT'
  | 'SYNC_SAP_MIRO'
  | 'CANCEL';

export interface InvoiceActionAvailability {
  action: InvoiceActionType;
  label: string;
  isAvailable: boolean;
  reason?: string;
  isDestructive?: boolean;
}

export function getAvailableInvoiceActions(
  invoice: InvoiceRecord,
  userPermissions: { can?: (perm: Permission) => boolean; role?: string } = {}
): InvoiceActionAvailability[] {
  const isTerminal = isTerminalInvoiceStatus(invoice.status);
  const canMatch = userPermissions.can ? userPermissions.can('INVOICE_MATCH') : true;

  const hasUnresolvedExceptions = invoice.exceptions.some((e) => e.status !== 'RESOLVED');

  return [
    {
      action: 'EXECUTE_MATCH',
      label: 'Run 3-Way Match',
      isAvailable: !isTerminal && invoice.status !== 'PAID' && canMatch,
      reason: isTerminal ? 'Invoice is in terminal state.' : undefined,
    },
    {
      action: 'APPROVE',
      label: 'Approve for Payment',
      isAvailable:
        !isTerminal &&
        invoice.status !== 'APPROVED' &&
        invoice.status !== 'READY_FOR_PAYMENT' &&
        invoice.status !== 'PAID' &&
        !hasUnresolvedExceptions &&
        canMatch,
      reason: hasUnresolvedExceptions
        ? 'Cannot approve invoice with active unresolved exceptions.'
        : isTerminal
        ? 'Invoice is finalized.'
        : undefined,
    },
    {
      action: 'MARK_READY_FOR_PAYMENT',
      label: 'Authorize for Payment',
      isAvailable:
        invoice.status === 'APPROVED' &&
        invoice.paymentStatus !== 'READY_FOR_PAYMENT' &&
        invoice.paymentStatus !== 'PAID' &&
        invoice.isReadyForPayment &&
        canMatch,
      reason: !invoice.isReadyForPayment ? 'Payment prerequisites not satisfied.' : undefined,
    },
    {
      action: 'RESOLVE_EXCEPTION',
      label: 'Resolve Exceptions',
      isAvailable: hasUnresolvedExceptions && !isTerminal && canMatch,
      reason: !hasUnresolvedExceptions ? 'No active exceptions to resolve.' : undefined,
    },
    {
      action: 'PLACE_ON_HOLD',
      label: 'Place on Hold',
      isAvailable: !isTerminal && invoice.status !== 'ON_HOLD' && invoice.status !== 'PAID' && canMatch,
    },
    {
      action: 'RELEASE_FROM_HOLD',
      label: 'Release from Hold',
      isAvailable: invoice.status === 'ON_HOLD' && canMatch,
    },
    {
      action: 'SYNC_SAP_MIRO',
      label: 'Post SAP MIRO (BAPI)',
      isAvailable: invoice.sapSyncStatus !== 'SYNCED' && !isTerminal,
      reason: invoice.sapSyncStatus === 'SYNCED' ? 'Already posted to SAP MIRO.' : undefined,
    },
    {
      action: 'REJECT',
      label: 'Reject Invoice',
      isAvailable: !isTerminal && invoice.status !== 'PAID' && canMatch,
      isDestructive: true,
      reason: isTerminal ? 'Cannot reject finalized invoice.' : undefined,
    },
    {
      action: 'CANCEL',
      label: 'Cancel Invoice',
      isAvailable: !isTerminal && invoice.status !== 'PAID' && canMatch,
      isDestructive: true,
      reason: isTerminal ? 'Cannot cancel finalized invoice.' : undefined,
    },
  ];
}
